import { staticError, useGuardedEffect } from "@p0security/frontend-core";
import { AggregatedGraph } from "@p0security/shared/graph/aggregate";
import {
  AnyAggregates,
  AssessmentNodes,
} from "@p0security/shared/types/assessment/data/index";
import { useObjectMemo } from "hooks/useObjectMemo";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useControls } from "../hooks/useControls";
import { aggregateGraphForShow } from "../utils/graph";
import { FindingsContext } from "./FindingsContext";
import { ScopeContext } from "./ScopeContext";

type AggregatedGraphContext = {
  aggregated?: AggregatedGraph<AssessmentNodes, AnyAggregates>;
};

export const AggregatedGraphContext = createContext<AggregatedGraphContext>({});

export const AggregatedGraphProvider: React.FC<
  React.PropsWithChildren<object>
> = ({ children }) => {
  const { controls, setControls } = useControls();
  const { graph } = useContext(ScopeContext);
  const { selectedMonitor: monitor, isValidMonitorScope } =
    useContext(FindingsContext);

  const [aggregated, setAggregated] = useState<
    AggregatedGraph<AssessmentNodes, AnyAggregates> | undefined
  >(undefined);

  const derivedShow = useMemo(
    () => monitor?.show ?? controls.show,
    [controls.show, monitor?.show]
  );

  useEffect(() => {
    if (controls.show !== derivedShow) {
      setControls({ show: derivedShow }, "replaceIn");
    }
  }, [controls, derivedShow, setControls]);

  // When either a new graph is available, or the "show" value changes, reaggregate the graph
  useGuardedEffect(
    (cancellation) => async () => {
      setAggregated(undefined);

      if (!graph || !isValidMonitorScope) return;
      aggregateGraphForShow({
        cancellation,
        graph,
        setAggregated,
        show: derivedShow,
      });
    },
    [derivedShow, graph, isValidMonitorScope],
    staticError
  );

  const value = useObjectMemo({ aggregated });

  return (
    <AggregatedGraphContext.Provider value={value}>
      {children}
    </AggregatedGraphContext.Provider>
  );
};
